import React, { useState, useEffect } from "react";
import styles from "./home.module.css";
import Header from "../../components/@header/header.js";
import Book from "../book/book.js";
import { getCoursesV2, getLanguagePack } from "../../service/service.js";
import ReactGA from "react-ga";
import SelectLanguage from "../selectLanguage/selectedLanguage.js";
import { getUser } from "../../service/service";
import Popup from "../popup/popup.js";

const Home = () => {
  const [courses, setCourses] = useState([]);
  const [coursesLoading, setCoursesLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState(getCurrentLanguage());
  const [languagePackage, setLanguagePackage] = useState(getLanguagePack());
  const [currentBook, setCurrentBook] = useState(null);
  const [studentName, setStudentName] = useState("Aluno");
  const [popupOpen, setPopupOpen] = useState(false);

  const fetchStudent = async () => {
    const userData = await getUser();
    setStudentName(userData.first_name);
  };

  useEffect(() => {
    fetchStudent();
  }, []);

  function getCurrentLanguage() {
    let data = localStorage.getItem("languagePack");
    return data == null ? "en" : JSON.parse(data).selectedLanguage;
  }

  const GREETINGS = {
    pt: "Olá",
    "pt-br": "Olá",
    en: "Hello",
    "en-us": "Hello",
    es: "Hola",
    fr: "Bonjour",
  };

  const FALLBACK_MESSAGES = {
    en: {
      "Estas são as aulas disponíveis para você fazer. Escolha uma para começar!":
        "These are the available classes for you. Choose one to get started!",
      "Carregando cursos...": "Loading courses...",
    },
    es: {
      "Estas são as aulas disponíveis para você fazer. Escolha uma para começar!":
        "Estas son las clases disponibles para ti. ¡Elige una para comenzar!",
      "Carregando cursos...": "Cargando cursos...",
    },
  };

  function getGreeting() {
    const lang = (currentLanguage || "pt").toLowerCase();
    return GREETINGS[lang] || GREETINGS[lang.split("-")[0]] || "Olá";
  }

  function _getMessage(message) {
    const lang = (currentLanguage || "pt").toLowerCase().split("-")[0];
    // Para idiomas não-PT, fallback hardcodado tem prioridade sobre o TaaS
    // (o TaaS pode retornar a chave em PT sem tradução)
    if (
      lang !== "pt" &&
      FALLBACK_MESSAGES[lang] &&
      FALLBACK_MESSAGES[lang][message]
    ) {
      return FALLBACK_MESSAGES[lang][message];
    }
    return languagePackage[message] || message;
  }

  const fetchCourses = async (wizmeId) => {
    try {
      const fetchedCourses = await getCoursesV2(wizmeId);
      const data = fetchedCourses.data.data.filter(
        (course) =>
          course.book &&
          course.book.language &&
          course.book.language.code === currentLanguage,
      );
      setCourses(data);
      setCoursesLoading(false);
    } catch (e) {
      console.log(e);
      setCourses([]);
    } finally {
      setCoursesLoading(false);
    }
  };

  useEffect(() => {
    let trackingId = "UA-1839981-59";
    let userInfo = localStorage.getItem("user-info");
    let userId = "undefined";

    if (userInfo) {
      try {
        const parsedUserInfo = JSON.parse(userInfo);
        const platformId =
          parsedUserInfo.wizmeId ||
          (parsedUserInfo.data && parsedUserInfo.data.wizmeId);
        if (parsedUserInfo && parsedUserInfo.id) {
          userId = parsedUserInfo.id;
        } else {
          console.log("ID not found");
        }

        if (platformId) {
          fetchCourses(platformId);
        } else {
          setCoursesLoading(false);
        }
      } catch (e) {
        console.log("Error parsing user info:", e);
      }
    } else {
      console.log("User info not found in local storage");
    }

    ReactGA.initialize(trackingId);
    ReactGA.set({ user_id: userId });
    ReactGA.event({ category: "Catchup", action: "Accessed Home Page" });
    ReactGA.event({ category: "Catchup", action: "Load Courses" });
  }, [currentLanguage]);

  // sets currentbook:
  //   if courses list is empty it does nothing
  //   if currentBook exists in localStorage, use it to set currentBook
  //   if it doesn't, set's the first item of Courses as the currentBook
  useEffect(() => {
    if (courses.length !== 0) {
      let currentBookId = localStorage.getItem("currentBookId");
      let book = null;
      if (courses.length > 1) {
        if (currentBookId !== null) {
          currentBookId = parseInt(currentBookId);
          const found = courses.find(
            (course) => course.book && course.book.id === currentBookId,
          );
          book = found ? found.book : courses[0].book;
        } else {
          book = courses[0].book;
        }
      } else {
        book = courses[0].book;
      }
      localStorage.setItem("currentBookId", JSON.stringify(book.id));
      localStorage.setItem("currentBookName", JSON.stringify(book.name));
      setCurrentBook(book);
    }
  }, [courses]);

  function closeModal() {
    setModalOpen(false);
  }

  function openModal() {
    setModalOpen(true);
  }

  const changeBook = (e) => {
    const selectedBookId = parseInt(e.target.value);
    const selectedBookObject = courses.find(
      (course) => course.book.id === selectedBookId,
    );
    localStorage.setItem(
      "currentBookId",
      JSON.stringify(selectedBookObject.book.id),
    );
    localStorage.setItem(
      "currentBookName",
      JSON.stringify(selectedBookObject.book.name),
    );
    setCurrentBook(selectedBookObject.book);
    window.location.reload(); // Force reload to update "completed" status on batches after book change
  };

  useEffect(() => {
    const hasLanguagePack = localStorage.getItem("languagePack");

    if (!hasLanguagePack && isWithinValidityPeriod()) {
      setPopupOpen(true);
    }
  }, []);

  const isWithinValidityPeriod = () => {
    const now = new Date();

    const start = new Date(2026, 0, 14); // 14/01/2026
    const end = new Date(2026, 2, 31, 23, 59, 59); // 31/03/2026

    return now >= start && now <= end;
  };

  return (
    <div>
      {modalOpen && <SelectLanguage closeModal={closeModal} />}
      {popupOpen && (
        <Popup popupOpen={popupOpen} onClose={() => setPopupOpen(false)} />
      )}
      <div>
        <Header menuLanguage={true} />
      </div>
      <div className="container-fluid d-flex home-container justify-content-center background-image">
        <div style={{ width: 1200 }}>
          <div>
            <div className={styles.welcome}>
              <h4 title="Aluno">{`${getGreeting()}, ${studentName}`}</h4>
              <p>
                {_getMessage(
                  "Estas são as aulas disponíveis para você fazer. Escolha uma para começar!",
                )}
              </p>
            </div>
          </div>
          <div
            className="card"
            style={{ marginBottom: 50, border: "none", background: "none" }}
          >
            <div>
              {coursesLoading && (
                <div className="loading-lessons-container">
                  <div className="spinner-border home-loading" role="status">
                    <span className="sr-only">Loading...</span>
                  </div>
                  <div style={{ color: "rgb(117,117,117)" }}>
                    {_getMessage("Carregando cursos...")}
                  </div>
                </div>
              )}
              {courses.length === 0 && !coursesLoading ? (
                <div className="home-no-lessons">No lessons available.</div>
              ) : (
                currentBook && (
                  <div className="all-lessons-container">
                    <Book
                      currentBook={currentBook}
                      courses={courses}
                      changeBook={changeBook}
                      currentLanguage={currentLanguage}
                    />
                  </div>
                )
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;
